/**
 * Page-view + feature-event ingestion clients for platform usage analytics.
 *
 * Designed to be strictly non-blocking and to never impact UX:
 *  - fire-and-forget from the caller (never awaited on the render path);
 *  - `priority: 'low'` so the browser schedules it behind real user requests;
 *  - a short abort timeout so a hung request can't hold a connection slot and
 *    starve the limited per-origin connection pool (which would block real API
 *    calls) — see https://fetch.spec.whatwg.org/#request-priority;
 *  - `keepalive` so the beacon survives tab close / navigation;
 *  - never throws — analytics is best-effort and must never disrupt the user.
 *
 * The server attributes org/user identity from the authenticated session.
 */

import { createFetchWithCsrfRetry, getCsrfHeaders } from '../utils/csrf'

const fetchWithCsrfRetry = createFetchWithCsrfRetry('/api/organizations-module/auth/csrf')

/** Max time a tracking request may stay in flight (bounds connection holding). */
const TRACKING_TIMEOUT_MS = 6000

export interface PageViewPayload {
  module_key: string
  page_key: string
  search?: string | null
}

export async function recordPageView(payload: PageViewPayload): Promise<void> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), TRACKING_TIMEOUT_MS)
  try {
    await fetchWithCsrfRetry(() =>
      fetch('/api/tracking/page-views', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
        body: JSON.stringify(payload),
        keepalive: true,
        priority: 'low',
        signal: controller.signal,
      }),
    )
  } catch {
    // Swallow every failure (network error, abort, CSRF refresh failure, etc.) —
    // page-view tracking is best-effort and must never surface to the user.
  } finally {
    clearTimeout(timeout)
  }
}

export interface FeatureEventPayload {
  feature_key: string
  target_id: string
  metadata?: Record<string, unknown>
}

/** Suppress an identical (featureKey, targetId) pair sent within this window —
 *  guards double-fire edges (apply + URL sync) without dropping genuine repeats. */
const FEATURE_EVENT_DEDUPE_MS = 3000
const recentFeatureEvents = new Map<string, number>()

/**
 * Record one use of a feature (fire-and-forget, best-effort, never throws).
 *
 * `featureKey` is a dotted namespace (`module.feature[.action]`) and `targetId`
 * the thing acted on; org/user identity is attributed server-side. Callers on
 * the render path use `void trackFeatureEvent(...)` — nothing awaits this.
 */
export function trackFeatureEvent(
  featureKey: string,
  targetId: string,
  metadata?: Record<string, unknown>,
): void {
  const dedupeKey = `${featureKey}:${targetId}`
  const now = Date.now()
  const last = recentFeatureEvents.get(dedupeKey)
  if (last !== undefined && now - last < FEATURE_EVENT_DEDUPE_MS) return
  recentFeatureEvents.set(dedupeKey, now)

  void (async () => {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), TRACKING_TIMEOUT_MS)
    try {
      await fetchWithCsrfRetry(() =>
        fetch('/api/tracking/feature-events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
          body: JSON.stringify({ feature_key: featureKey, target_id: targetId, metadata }),
          keepalive: true,
          priority: 'low',
          signal: controller.signal,
        }),
      )
    } catch {
      // Swallow every failure — feature tracking must never surface to the user.
    } finally {
      clearTimeout(timeout)
    }
  })()
}
